import { useParams, Link } from "react-router-dom";
import { articles } from "@/mocks/articles";
import ScrollToTop from "@/components/base/ScrollToTop";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import SchemaOrg from "@/components/base/SchemaOrg";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  const article = articles.find((a) => a.id === id);
  const relatedArticles = articles
    .filter((a) => a.id !== id)
    .slice(0, 3);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
            <h1 className="text-2xl font-bold font-heading text-foreground-950">文章不存在</h1>
            <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const breadcrumbItems = [
    { label: "首页", href: "/" },
    { label: article.categoryName, href: `/category/${article.categorySlug}` },
    { label: article.title },
  ];

  const articleSchema: Record<string, unknown> = {
    url: `https://91shequ.com/article/${article.id}`,
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: article.publishDate,
    author: {
      "@type": "Person",
      name: article.author,
    },
    publisher: {
      "@type": "Organization",
      name: "91社区",
    },
  };

  const breadcrumbSchema = {
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://91shequ.com" },
      { "@type": "ListItem", position: 2, name: article.categoryName, item: `https://91shequ.com/category/${article.categorySlug}` },
      { "@type": "ListItem", position: 3, name: article.title, item: `https://91shequ.com/article/${article.id}` },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SchemaOrg type="Article" data={articleSchema} />
      <SchemaOrg type="BreadcrumbList" data={breadcrumbSchema} />
      <SchemaOrg
        type="WebPage"
        data={{
          name: `${article.title} - 91社区`,
          description: article.excerpt,
          url: `https://91shequ.com/article/${article.id}`,
        }}
      />

      <Navbar />
      <main className="min-h-screen pt-20">
        <article className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          <header className="py-8">
            <div className="flex items-center gap-3 mb-4">
              <Link
                to={`/category/${article.categorySlug}`}
                className="text-xs px-3 py-1 rounded-full bg-accent-100 text-accent-700 font-medium hover:bg-accent-200 transition-colors"
              >
                {article.categoryName}
              </Link>
              <span className="text-xs text-foreground-500">{article.publishDate}</span>
            </div>

            <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-950 leading-tight">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 mt-5 pb-6 border-b border-background-200/70">
              <div className="flex items-center gap-2">
                <img
                  src={article.authorAvatar}
                  alt={article.author}
                  className="w-8 h-8 rounded-full object-cover"
                />
                <span className="text-sm font-medium text-foreground-800">{article.author}</span>
              </div>
              <div className="flex items-center gap-4 text-xs text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i> {article.views.toLocaleString()} 阅读
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-heart-line"></i> {article.likes.toLocaleString()} 点赞
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-chat-3-line"></i> {article.comments} 评论
                </span>
              </div>
            </div>
          </header>

          <div className="mb-8 rounded-xl overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-auto object-cover max-h-[450px]"
            />
          </div>

          <div
            className="article-content pb-8 border-b border-background-200/70"
            dangerouslySetInnerHTML={{ __html: article.contentHtml }}
          />

          <div className="flex flex-wrap gap-2 py-6">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium"
              >
                #{tag}
              </span>
            ))}
          </div>

          <div className="flex items-center justify-between py-6 border-t border-background-200/70">
            <button className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-50 text-primary-600 hover:bg-primary-100 transition-colors text-sm font-medium cursor-pointer whitespace-nowrap">
              <i className="ri-heart-line"></i> 点赞 ({article.likes.toLocaleString()})
            </button>
            <div className="flex items-center gap-2">
              <button className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-500 transition-colors cursor-pointer" aria-label="分享到微信">
                <i className="ri-wechat-line text-lg"></i>
              </button>
              <button className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-500 transition-colors cursor-pointer" aria-label="分享到微博">
                <i className="ri-weibo-line text-lg"></i>
              </button>
              <button className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-500 transition-colors cursor-pointer" aria-label="复制链接">
                <i className="ri-link text-lg"></i>
              </button>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="max-w-4xl mx-auto px-4 md:px-6 pb-16">
            <h2 className="text-xl font-bold font-heading text-foreground-950 mb-6">
              相关推荐
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {relatedArticles.map((ra) => (
                <Link
                  key={ra.id}
                  to={`/article/${ra.id}`}
                  className="group flex flex-col rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer overflow-hidden"
                >
                  <div className="w-full h-36 overflow-hidden">
                    <img
                      src={ra.image}
                      alt={ra.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-3">
                    <h3 className="text-sm font-bold font-heading text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {ra.title}
                    </h3>
                    <div className="flex items-center gap-2 mt-2 text-xs text-foreground-500">
                      <span>{ra.categoryName}</span>
                      <span>·</span>
                      <span>{ra.publishDate}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}